import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useResetPasswordMutation } from '@/redux/internal-services/onboardingApi';
import LeftSidePanel from '@/components/UI/LeftSidePanel';

const ResetPasswordPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';
  const [resetPassword, { isLoading }] = useResetPasswordMutation();

  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (newPassword !== confirm) {
      setError(t('auth.passwordMismatch', 'Passwords do not match.'));
      return;
    }
    if (!token) {
      setError(t('auth.invalidResetToken', 'Invalid or missing reset token.'));
      return;
    }
    try {
      await resetPassword({ token, new_password: newPassword }).unwrap();
      setDone(true);
      setTimeout(() => navigate('/login'), 3000);
    } catch (err: unknown) {
      const apiErr = err as { data?: { message_code?: number } };
      if (apiErr?.data?.message_code === 1119) {
        setError(t('messages.1119', 'New password must be different from your current password.'));
        return;
      }
      if (apiErr?.data?.message_code === 1115) {
        setError(t('auth.invalidResetToken', 'This reset link has expired or already been used. Please request a new one.'));
        return;
      }
      setError(t('common.error', 'Something went wrong. Please try again.'));
    }
  };

  const EyeIcon: React.FC<{ open: boolean }> = ({ open }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} className="w-4 h-4 text-ui-text3">
      {open ? (
        <>
          <path strokeLinecap="round" strokeLinejoin="round" d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
          <circle cx="12" cy="12" r="3" strokeLinecap="round" strokeLinejoin="round" />
        </>
      ) : (
        <>
          <path strokeLinecap="round" strokeLinejoin="round" d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24" />
          <line x1="1" y1="1" x2="23" y2="23" strokeLinecap="round" />
        </>
      )}
    </svg>
  );

  return (
    <div className="force-light flex min-h-screen">
      <LeftSidePanel />

      <div className="flex-1 flex items-center justify-center bg-ui-raised px-6 py-12">
        <div className="w-full max-w-md">

          {done ? (
            <div className="bg-ui-card rounded-2xl shadow-md px-10 py-10 text-center">
              {/* Success icon */}
              <div className="w-16 h-16 bg-brand-light rounded-full flex items-center justify-center mx-auto mb-6">
                <svg viewBox="0 0 24 24" fill="none" className="w-8 h-8 text-brand-dark" stroke="currentColor" strokeWidth={1.8}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>

              <h2 className="text-2xl font-bold text-ui-text mb-2">
                {t('auth.passwordUpdated', 'Password Updated!')}
              </h2>
              <p className="text-ui-text2 text-sm mb-8">
                {t('auth.passwordResetSuccess', 'Your password has been reset. Redirecting you to login…')}
              </p>

              <button
                type="button"
                onClick={() => navigate('/login')}
                className="w-full bg-[#1c1849] text-white py-3 rounded-lg font-semibold text-sm hover:bg-[#2d2b6b] transition"
              >
                {t('auth.goToLogin', 'Go to Login')}
              </button>
            </div>
          ) : (
            <>
              {/* Back link */}
              <button
                type="button"
                onClick={() => navigate('/login')}
                className="flex items-center gap-1.5 text-sm text-ui-text2 hover:text-[#5b21b6] mb-8 transition-colors"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                </svg>
                {t('auth.backToLogin', 'Back to login')}
              </button>

              <h1 className="text-2xl font-bold text-[#5b21b6] mb-2">
                {t('auth.resetPassword', 'Reset Password')}
              </h1>
              <p className="text-ui-text2 text-sm mb-8">
                {t('auth.resetPasswordDesc', "Choose a strong password you haven't used before.")}
              </p>

              {!token && (
                <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 mb-6">
                  <p className="text-red-600 text-sm">
                    {t('auth.invalidResetToken', 'Invalid or missing reset token. Please request a new link.')}
                  </p>
                  <button
                    type="button"
                    onClick={() => navigate('/forgot-password')}
                    className="mt-2 text-sm text-[#5b21b6] hover:underline font-medium"
                  >
                    {t('auth.requestNewLink', 'Request new link →')}
                  </button>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-ui-text mb-1.5">
                    {t('auth.newPassword', 'New Password')}
                  </label>
                  <div className="relative">
                    <input
                      type={showNew ? 'text' : 'password'}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      required
                      minLength={8}
                      placeholder="Min. 8 characters"
                      className="w-full border border-ui-border rounded-lg px-3 py-2.5 pr-10 text-sm bg-ui-input focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                    <button
                      type="button"
                      onMouseDown={() => setShowNew(true)}
                      onMouseUp={() => setShowNew(false)}
                      onMouseLeave={() => setShowNew(false)}
                      onTouchStart={() => setShowNew(true)}
                      onTouchEnd={() => setShowNew(false)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 focus:outline-none"
                      tabIndex={-1}
                      aria-label="Hold to show password"
                    >
                      <EyeIcon open={showNew} />
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-ui-text mb-1.5">
                    {t('auth.confirmPassword', 'Confirm Password')}
                  </label>
                  <div className="relative">
                    <input
                      type={showConfirm ? 'text' : 'password'}
                      value={confirm}
                      onChange={(e) => setConfirm(e.target.value)}
                      required
                      placeholder="Re-enter your password"
                      className="w-full border border-ui-border rounded-lg px-3 py-2.5 pr-10 text-sm bg-ui-input focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                    <button
                      type="button"
                      onMouseDown={() => setShowConfirm(true)}
                      onMouseUp={() => setShowConfirm(false)}
                      onMouseLeave={() => setShowConfirm(false)}
                      onTouchStart={() => setShowConfirm(true)}
                      onTouchEnd={() => setShowConfirm(false)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 focus:outline-none"
                      tabIndex={-1}
                      aria-label="Hold to show password"
                    >
                      <EyeIcon open={showConfirm} />
                    </button>
                  </div>
                  {confirm && newPassword !== confirm && (
                    <p className="text-red-500 text-xs mt-1">
                      {t('auth.passwordMismatch', 'Passwords do not match.')}
                    </p>
                  )}
                </div>

                {error && (
                  <p className="text-red-500 text-sm">{error}</p>
                )}

                <button
                  type="submit"
                  disabled={isLoading || !token}
                  className="w-full bg-[#1c1849] text-white py-2.5 rounded-lg text-sm font-medium hover:bg-[#2d2b6b] transition disabled:opacity-50"
                >
                  {isLoading ? t('common.loading', 'Updating…') : t('auth.resetPassword', 'Reset Password')}
                </button>
              </form>
            </>
          )}

        </div>
      </div>
      </div>
  );
};

export default ResetPasswordPage;
